/**
 * MFA formatting utilities
 * Shared functions for formatting email addresses and phone numbers for MFA display
 */

import { maskEmail } from "./helpers";

/**
 * Process email address for display.
 * - If API already masked with dots, converts dots to asterisks for consistent display.
 * - If already masked with asterisks, returns as-is.
 * - Otherwise delegates to maskEmail (single source of masking logic).
 * Example: "s...1@freedommortgage.com" -> "s****1@freedommortgage.com"
 * Example: "alex@example.com" -> uses maskEmail (e.g. "a******x@example.com")
 */
export function processEmailForDisplay(email: string): string {
  if (!email || typeof email !== "string") {
    return email;
  }

  const atIndex = email.indexOf("@");
  if (atIndex > 0) {
    const localPart = email.slice(0, atIndex);

    if (localPart.includes("...") || localPart.includes("..")) {
      return email.replace(/\.\.\./g, "****").replace(/\.\./g, "**");
    }

    if (localPart.includes("*")) {
      return email;
    }
  }

  return maskEmail(email);
}

/** Zero-width space (U+200B) – allows line break at this point for responsive wrapping */
const LINE_BREAK_OPPORTUNITY = "\u200B";

/**
 * Insert a line-break opportunity before @ so masked email wraps gracefully on mobile.
 * Result wraps as "[a****d" / "@freedommortgage.com]" instead of mid-domain.
 */
export function addEmailLineBreakOpportunity(maskedEmail: string): string {
  if (!maskedEmail || typeof maskedEmail !== "string") {
    return maskedEmail;
  }
  const atIndex = maskedEmail.indexOf("@");
  if (atIndex <= 0) {
    return maskedEmail;
  }
  return `${maskedEmail.slice(0, atIndex)}${LINE_BREAK_OPPORTUNITY}@${maskedEmail.slice(atIndex + 1)}`;
}

/**
 * Extract last 4 digits from phone number, handling masked numbers
 * Example: "+1XXXXXX9381" -> "9381"
 * Example: "+15551234567" -> "4567"
 * Returns "" for null, undefined, or non-string input.
 */
export function extractLastFourDigits(phoneNumber: string | null | undefined): string {
  if (phoneNumber == null || typeof phoneNumber !== "string") {
    return "";
  }
  const digits = phoneNumber.replace(/\D/g, "");
  return digits.slice(-4);
}

/**
 * Format phone number for display: ***-***-XXXX
 * Returns "" for null, undefined, or non-string input.
 */
export function formatPhoneForDisplay(phoneNumber: string | null | undefined): string {
  if (phoneNumber == null || typeof phoneNumber !== "string") {
    return "";
  }
  const lastFour = extractLastFourDigits(phoneNumber);
  return `***-***-${lastFour}`;
}
